import Image from "next/image";

import cn from "classnames";

import { Link } from "@/components/link/link.component";
import { RichText } from "@/components/richText/richText.component";

import { resolveLinkHref } from "@/utils/url.util";

import { brown } from "@/app/font";
import type { GetHomePageQueryResult } from "@/sanity.types";

export const AdtQuizPromo = ({ block }: { block: QuizPromoBlock }) => {
  const imageOnRight = block.imagePosition === "right";

  const renderCtas = () => {
    return (block.ctas ?? []).map((cta, index) => (
      <Link
        key={cta._key}
        secondary
        border={index > 0}
        href={resolveLinkHref(cta.link)}
        className={
          index === 0
            ? "min-h-16 min-w-[268px] bg-[#2d68ad]! px-8 py-4"
            : "min-h-16 min-w-[242px] border-[#2d68ad]! px-8 py-4 text-[#2d68ad]!"
        }>
        {cta.label}
      </Link>
    ));
  };

  return (
    <section
      aria-labelledby="adt-quiz-promo-title"
      className="w-full overflow-x-hidden bg-white py-10 xl:px-14 xl:py-11 xl:pb-[67px]">
      <div
        data-testid="adt-quiz-promo-layout"
        className="mx-auto grid max-w-[1750px] grid-cols-1 items-center gap-8 xl:grid-cols-2 xl:gap-10">
        {block.image?.url || block.mobileImage?.url ? (
          <div
            data-testid="adt-quiz-promo-media"
            className={cn("w-full overflow-hidden xl:rounded-[22px]", {
              "xl:order-2": imageOnRight,
            })}>
            {block.mobileImage?.url ? (
              <Image
                src={block.mobileImage.url}
                alt={block.mobileImage.alt || block.image?.alt || ""}
                width={1328}
                height={752}
                sizes="100vw"
                className={cn(
                  "h-auto w-full max-xl:origin-center max-xl:scale-[1.06] xl:rounded-[22px]",
                  { "xl:hidden": block.image?.url },
                )}
              />
            ) : null}
            {block.image?.url ? (
              <Image
                src={block.image.url}
                alt={block.image.alt || ""}
                width={1328}
                height={752}
                sizes="(min-width: 1280px) 50vw, 100vw"
                className={cn(
                  "h-auto w-full max-xl:origin-center max-xl:scale-[1.06] xl:rounded-[22px]",
                  { "hidden xl:block": block.mobileImage?.url },
                )}
              />
            ) : null}
          </div>
        ) : null}

        <div
          className={cn(
            "flex max-w-[720px] flex-col items-start px-6 sm:px-10 xl:px-0",
            { "xl:order-2": !imageOnRight },
          )}>
          <p className="subtitle-1 text-adt-blue">{block.eyebrow}</p>
          <h2
            id="adt-quiz-promo-title"
            className={`${brown.className} text-greyscale-headline mt-4 text-[32px] leading-[1.2] font-bold sm:text-[48px]`}>
            {block.title}
          </h2>
          <p className="text-greyscale-body mt-6 max-w-[720px] text-base leading-[1.4] sm:text-[20px] xl:text-[22px]">
            {block.description}
          </p>
          <div
            data-testid="adt-quiz-promo-actions"
            className="mt-10 flex w-full flex-col gap-4 sm:mt-12 sm:w-auto sm:flex-row sm:gap-6 xl:mt-10">
            {renderCtas()}
          </div>
          {block.legal && (
            <div
              data-testid="adt-quiz-promo-legal"
              className="text-greyscale-body mt-6 w-full max-w-[720px]">
              <RichText>{block.legal}</RichText>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

type QuizPromoBlock = Extract<
  NonNullable<NonNullable<GetHomePageQueryResult>["pageBuilder"]>[number],
  { _type: "adtQuizPromo" }
>;
